interface ImpersonationSession {
  accessToken: string;
  refreshToken: string;
  brandId: string;
  brandName: string;
}

let accessToken: string | null = null;
let impersonation: ImpersonationSession | null = null;

// Survives a browser reload (tokens live in memory only, so they don't).
// Deliberately holds NO secrets — just which brand the admin was viewing;
// the tokens are re-minted from the admin session on bootstrap.
const PINNED_BRAND_KEY = 'impersonate_brand';

const pinBrand = (session: ImpersonationSession | null): void => {
  try {
    if (session) {
      sessionStorage.setItem(
        PINNED_BRAND_KEY,
        JSON.stringify({
          brandId: session.brandId,
          brandName: session.brandName,
        }),
      );
    } else {
      sessionStorage.removeItem(PINNED_BRAND_KEY);
    }
  } catch {
    // Storage disabled (private mode / blocked cookies): impersonation still
    // works, it just won't survive a reload.
  }
};

export const TokenStore = {
  getAccessToken(): string | null {
    return accessToken;
  },

  setAccessToken(token: string | null): void {
    accessToken = token;
  },

  isImpersonating(): boolean {
    return impersonation !== null;
  },

  getImpersonationSession(): ImpersonationSession | null {
    return impersonation;
  },

  getImpersonationAccessToken(): string | null {
    return impersonation?.accessToken ?? null;
  },

  getImpersonationRefreshToken(): string | null {
    return impersonation?.refreshToken ?? null;
  },

  setImpersonationSession(session: ImpersonationSession | null): void {
    impersonation = session;
    pinBrand(session);
  },

  /** Brand the admin was impersonating before a reload, if any. */
  getPinnedBrand(): { brandId: string; brandName: string } | null {
    try {
      const raw = sessionStorage.getItem(PINNED_BRAND_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.brandId ? parsed : null;
    } catch {
      return null;
    }
  },

  /** The token that should actually be sent on API requests right now. */
  getActiveAccessToken(): string | null {
    return impersonation?.accessToken ?? accessToken;
  },

  setActiveAccessToken(token: string): void {
    if (impersonation) {
      impersonation = { ...impersonation, accessToken: token };
    } else {
      accessToken = token;
    }
  },

  clear(): void {
    accessToken = null;
    impersonation = null;
    pinBrand(null);
  },
};
